import { gql } from "_graphql-types/gql";
import type { UnifiedFilterData, FilterItem } from "./UnifiedFilters.component";
import { ENTITY_COLORS } from "./constants";
import type { AntdTreeNode } from "frontend/src/components/Tree/treeData";
import React, { ReactNode } from "react";
import Icon from "frontend/src/icons/Icon";
import CheckOutlined from "@mui/icons-material/Check";
import StopOutlined from "@mui/icons-material/Block";

// Icon mapping for impact categories (matching ImpactFilter.tsx exactly)
// Note: This function returns ReactNode icons to avoid JSX in .ts file
export const getImpactCategoryIcon = (categoryId: number): ReactNode => {
  const icons: { [key: number]: ReactNode } = {
    1: React.createElement(CheckOutlined),
    2: React.createElement(Icon, {
      component: () =>
        React.createElement("i", { className: "icon icon-fund" }),
    }),
    3: React.createElement(Icon, {
      component: () =>
        React.createElement("i", { className: "icon icon-wind-turbine" }),
    }),
    4: React.createElement(Icon, {
      component: () =>
        React.createElement("i", { className: "icon icon-mountains" }),
    }),
    5: React.createElement(Icon, {
      component: () => React.createElement("i", { className: "fa fa-eye" }),
    }),
    6: React.createElement(Icon, {
      component: () =>
        React.createElement("i", { className: "icon icon-agriculture" }),
    }),
    7: React.createElement(Icon, {
      component: () =>
        React.createElement("i", { className: "icon icon-library" }),
    }),
    8: React.createElement(Icon, {
      component: () =>
        React.createElement("i", { className: "icon icon-coin-dollar" }),
    }),
    9: React.createElement(Icon, {
      component: () =>
        React.createElement("i", { className: "icon icon-graduation-cap" }),
    }),
    10: React.createElement(StopOutlined),
  };
  return icons[categoryId] || null;
};

// Queries for loading filter options upfront
export const DOCUMENT_TYPE_ENUM_QUERY = gql(/* GraphQL */ `
  query DocumentTypeEnumList {
    documentTypeEnumList {
      items {
        id
        name
        documentCategoryEnumId
      }
    }
  }
`);

export const NOTE_META_ENUM_QUERY = gql(/* GraphQL */ `
  query NoteMetaTree {
    noteCategoryEnumList {
      items {
        id
        noteCategory
        noteTypes {
          id
          noteType
          noteMetaEnums {
            id
            label
          }
        }
      }
    }
  }
`);

export const KNOWLEDGE_BASE_TAGS_QUERY = gql(/* GraphQL */ `
  query KnowledgeBaseTagsOptions($filter: KnowledgeBaseTagsFilter) {
    knowledgeBaseTagsList(
      filter: $filter
      sort: [{ field: id, order: ASC }]
    ) {
      items {
        id
        name
        description
      }
    }
  }
`);

export const ASSET_CLASS_QUERY = gql(/* GraphQL */ `
  query AssetClassOptionsQuery {
    typeAssetClassStrategyOptionsList {
      items {
        assetClass {
          id
          name
        }
        strategy {
          id
          name
          subStrategies {
            id
            name
          }
        }
      }
    }
  }
`);

export const THEME_OPTIONS_QUERY = gql(/* GraphQL */ `
  query ThemeOptionsQuery {
    impactCategoryEnumList {
      items {
        id
        name
        impactEnums {
          id
          name
        }
      }
    }
  }
`);

export const GEOGRAPHY_OPTIONS_QUERY = gql(/* GraphQL */ `
  query GeographyOptionsQuery {
    geographicFocusEnumList {
      items {
        id
        name
      }
    }
  }
`);

export const INVESTMENT_TYPE_OPTIONS_QUERY = gql(/* GraphQL */ `
  query InvestmentTypeOptionsQuery {
    typeAssetClassStrategyOptionsList {
      items {
        vehicleType {
          id
          name
        }
      }
    }
  }
`);

export const INDUSTRY_OPTIONS_QUERY = gql(/* GraphQL */ `
  query IndustryOptionsQuery($filter: TagFilterInput) {
    tagList(
      filter: $filter
      page: { limit: 200 }
      sort: [{ field: name, order: ASC }]
    ) {
      items {
        id
        name
      }
    }
  }
`);

export const INVESTMENT_TAGS_OPTIONS_QUERY = gql(/* GraphQL */ `
  query InvestmentTagsOptionsQuery($filter: TagFilterInput!) {
    tagList(
      filter: $filter
      page: { limit: 200 }
      sort: [{ field: tagClassName, order: ASC }]
    ) {
      items {
        id
        name
        tagClass {
          id
          name
        }
      }
    }
  }
`);

export const INVESTMENT_LIST_OPTIONS_QUERY = gql(/* GraphQL */ `
  query InvestmentListOptionsQuery {
    investmentSetList(page: { limit: 500 }) {
      items {
        id
        name
      }
    }
  }
`);

export interface FilterDefinition {
  id: string;
  label: string;
  category: string;
  categoryColor: string;
  filterData: UnifiedFilterData;
}

/**
 * Builds the Asset Class filter from GraphQL data
 * Data structure: Asset Class > Strategy > Sub-Strategies
 */
export function buildAssetClassFilter(data: any): FilterDefinition | null {
  if (!data?.typeAssetClassStrategyOptionsList?.items) return null;

  // Build tree structure: Asset Class > Strategy > Sub-Strategy
  const assetClassMap = new Map<
    number,
    {
      id: number;
      name: string;
      strategies: Map<
        number,
        {
          id: number;
          name: string;
          subStrategies: Array<{ id: number; name: string }>;
        }
      >;
    }
  >();

  // Group data by asset class and strategy
  data.typeAssetClassStrategyOptionsList.items.forEach((item: any) => {
    if (!item.assetClass) return;

    let assetClass = assetClassMap.get(item.assetClass.id);
    if (!assetClass) {
      assetClass = {
        id: item.assetClass.id,
        name: item.assetClass.name,
        strategies: new Map(),
      };
      assetClassMap.set(item.assetClass.id, assetClass);
    }

    if (item.strategy) {
      let strategy = assetClass.strategies.get(item.strategy.id);
      if (!strategy) {
        strategy = {
          id: item.strategy.id,
          name: item.strategy.name,
          subStrategies: item.strategy.subStrategies || [],
        };
        assetClass.strategies.set(item.strategy.id, strategy);
      }
    }
  });

  // Build tree and key mapping
  const keyToLabel = new Map<string, string>();
  const treeData = Array.from(assetClassMap.values())
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(assetClass => {
      const assetClassKey = `${assetClass.id}`;
      keyToLabel.set(assetClassKey, assetClass.name);

      const children = Array.from(assetClass.strategies.values())
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(strategy => {
          const strategyKey = `${assetClass.id}^${strategy.id}`;
          keyToLabel.set(strategyKey, strategy.name);

          const subChildren =
            strategy.subStrategies.length > 0
              ? [...strategy.subStrategies]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(subStrategy => {
                    const subStrategyKey = `${assetClass.id}^${strategy.id}^${subStrategy.id}`;
                    keyToLabel.set(subStrategyKey, subStrategy.name);
                    return {
                      key: subStrategyKey,
                      title: subStrategy.name,
                    };
                  })
              : undefined;

          return {
            key: strategyKey,
            title: strategy.name,
            children: subChildren,
          };
        });

      return {
        key: assetClassKey,
        title: assetClass.name,
        children: children.length > 0 ? children : undefined,
      };
    });

  return {
    id: "assetClass",
    label: "Asset Class",
    category: "Investment",
    categoryColor: ENTITY_COLORS.investment,
    filterData: {
      type: "multiSelectTree",
      filterKey: "assetClasses",
      label: "Asset Class",
      treeData,
      keyToLabel,
    },
  };
}

/**
 * Builds the Theme filter from GraphQL data (Impact Categories)
 * Data structure: Impact Category > Impact Enums
 */
export function buildThemeFilter(data: any): FilterDefinition | null {
  if (!data?.impactCategoryEnumList?.items) return null;

  const treeData: AntdTreeNode[] = [];
  const keyToLabel = new Map<string, string>();

  data.impactCategoryEnumList.items.forEach((category: any) => {
    const categoryKey = `theme-cat-${category.id}`;
    keyToLabel.set(categoryKey, category.name);

    const children: AntdTreeNode[] = category.impactEnums.map((impact: any) => {
      const impactKey = `theme-${impact.id}`;
      keyToLabel.set(impactKey, impact.name);

      return {
        title: impact.name,
        key: impactKey,
      };
    });

    const icon = getImpactCategoryIcon(category.id);
    treeData.push({
      title: icon
        ? React.createElement(React.Fragment, null, icon, " ", category.name)
        : category.name,
      key: categoryKey,
      icon,
      children,
    });
  });

  return {
    id: "theme",
    label: "Theme",
    category: "Investment",
    categoryColor: ENTITY_COLORS.investment,
    filterData: {
      type: "multiSelectTree",
      filterKey: "themes",
      label: "Theme",
      treeData,
      keyToLabel,
    },
  };
}

/**
 * Builds the Geography filter from GraphQL data
 */
export function buildGeographyFilter(data: any): FilterDefinition | null {
  if (!data?.geographicFocusEnumList?.items) return null;

  const items: FilterItem[] = data.geographicFocusEnumList.items.map(
    (geo: any) => ({
      id: geo.id,
      label: geo.name,
    })
  );

  return {
    id: "geography",
    label: "Geography",
    category: "Investment",
    categoryColor: ENTITY_COLORS.investment,
    filterData: {
      type: "multiSelect",
      filterKey: "geographies",
      label: "Geography",
      items,
    },
  };
}

/**
 * Builds the Investment Type filter from GraphQL data
 */
export function buildInvestmentTypeFilter(data: any): FilterDefinition | null {
  if (!data?.typeAssetClassStrategyOptionsList?.items) return null;

  const uniqueTypes = new Map<number, { id: number; name: string }>();
  data.typeAssetClassStrategyOptionsList.items.forEach((item: any) => {
    if (item.vehicleType && !uniqueTypes.has(item.vehicleType.id)) {
      uniqueTypes.set(item.vehicleType.id, item.vehicleType);
    }
  });

  const items: FilterItem[] = Array.from(uniqueTypes.values()).map(type => ({
    id: type.id,
    label: type.name,
  }));

  return {
    id: "investmentType",
    label: "Investment Type",
    category: "Investment",
    categoryColor: ENTITY_COLORS.investment,
    filterData: {
      type: "multiSelect",
      filterKey: "investmentTypes",
      label: "Investment Type",
      items,
    },
  };
}

/**
 * Builds the Document Type filter as a tree from GraphQL data
 * Data structure: Document Category > Document Types
 */
export function buildDocumentTypeFilter(data: any): FilterDefinition | null {
  if (!data?.documentTypeEnumList?.items) return null;

  // Map category IDs to descriptive names based on actual usage
  const categoryNames: { [key: number]: string } = {
    1: "Periodic Documents", // PERIODIC_DOC_CATEGORY
    2: "Due Diligence", // DILIGENCE_DOC_CATEGORY
    4: "Client Documents", // CLIENT_DOC_CATEGORY
  };

  // Group document types by category
  const categoryMap = new Map<number, Array<{ id: number; name: string }>>();

  data.documentTypeEnumList.items.forEach((docType: any) => {
    if (docType.documentCategoryEnumId) {
      const categoryId = docType.documentCategoryEnumId;
      if (!categoryMap.has(categoryId)) {
        categoryMap.set(categoryId, []);
      }
      categoryMap.get(categoryId)!.push({
        id: docType.id,
        name: docType.name,
      });
    }
  });

  // Build tree structure
  const treeData: AntdTreeNode[] = [];
  const keyToLabel = new Map<string, string>();

  Array.from(categoryMap.entries())
    .sort(([a], [b]) => a - b) // Sort by category ID
    .forEach(([categoryId, docTypes]) => {
      const categoryKey = `doc-cat-${categoryId}`;
      const categoryName =
        categoryNames[categoryId] || `Category ${categoryId}`;
      keyToLabel.set(categoryKey, categoryName);

      const children: AntdTreeNode[] = docTypes
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(docType => {
          const docTypeKey = `doc-type-${docType.id}`;
          keyToLabel.set(docTypeKey, docType.name);
          return {
            title: docType.name,
            key: docTypeKey,
          };
        });

      treeData.push({
        title: categoryName,
        key: categoryKey,
        children,
      });
    });

  if (treeData.length === 0) return null;

  return {
    id: "documentType",
    label: "Document Type",
    category: "Documents",
    categoryColor: ENTITY_COLORS.document,
    filterData: {
      type: "multiSelectTree",
      filterKey: "documentCategories",
      label: "Document Type",
      treeData,
      keyToLabel,
    },
  };
}

/**
 * Builds the Document Format filter with static file extension options
 */
export function buildDocumentFormatFilter(): FilterDefinition {
  const items: FilterItem[] = [
    { id: "pdf", label: "PDF" },
    { id: "doc", label: "Word (DOC)" },
    { id: "docx", label: "Word (DOCX)" },
    { id: "xls", label: "Excel (XLS)" },
    { id: "xlsx", label: "Excel (XLSX)" },
    { id: "ppt", label: "PowerPoint (PPT)" },
    { id: "pptx", label: "PowerPoint (PPTX)" },
    { id: "txt", label: "Text" },
    { id: "csv", label: "CSV" },
  ];

  return {
    id: "documentFormat",
    label: "Document Format",
    category: "Documents",
    categoryColor: ENTITY_COLORS.document,
    filterData: {
      type: "multiSelect",
      filterKey: "documentFormats",
      label: "Document Format",
      items,
    },
  };
}

/**
 * Builds the Note Meta filter with tree structure: Category > Meta (excluding Comment category)
 */
export function buildNoteMetaFilter(data: any): FilterDefinition | null {
  if (!data?.noteCategoryEnumList?.items) return null;

  const treeData: AntdTreeNode[] = [];
  const keyToLabel = new Map<string, string>();

  data.noteCategoryEnumList.items.forEach((category: any) => {
    // Skip Comment category
    if (category.noteCategory === "Comment") return;

    if (!category.noteTypes || category.noteTypes.length === 0) return;

    const categoryKey = `note-cat-${category.id}`;
    keyToLabel.set(categoryKey, category.noteCategory);

    const metaChildren: AntdTreeNode[] = [];

    // Flatten all note meta from all note types under this category
    category.noteTypes.forEach((type: any) => {
      if (!type.noteMetaEnums || type.noteMetaEnums.length === 0) return;

      type.noteMetaEnums.forEach((meta: any) => {
        const metaKey = `note-meta-${meta.id}`;
        keyToLabel.set(metaKey, meta.label);
        metaChildren.push({
          title: meta.label,
          key: metaKey,
        });
      });
    });

    if (metaChildren.length > 0) {
      treeData.push({
        title: category.noteCategory,
        key: categoryKey,
        children: metaChildren,
      });
    }
  });

  if (treeData.length === 0) return null;

  return {
    id: "noteMeta",
    label: "Note Meta",
    category: "Note",
    categoryColor: ENTITY_COLORS.note,
    filterData: {
      type: "multiSelectTree",
      filterKey: "noteMetaIds",
      label: "Note Meta",
      treeData,
      keyToLabel,
    },
  };
}

/**
 * Builds the Knowledge Base Tags filter from GraphQL data
 */
export function buildKnowledgeBaseTagsFilter(data: any): FilterDefinition | null {
  if (!data?.knowledgeBaseTagsList?.items) return null;

  const items: FilterItem[] = data.knowledgeBaseTagsList.items.map((tag: any) => ({
    id: tag.id,
    label: tag.name,
  }));

  if (items.length === 0) return null;

  return {
    id: "knowledgeBaseTags",
    label: "Knowledge Base Tags",
    category: "Knowledge Base",
    categoryColor: ENTITY_COLORS.knowledgeBase,
    filterData: {
      type: "multiSelect",
      filterKey: "knowledgeBaseTagIds",
      label: "Knowledge Base Tags",
      items,
    },
  };
}

/**
 * Builds the Industry filter from GraphQL data
 */
export function buildIndustryFilter(data: any): FilterDefinition | null {
  if (!data?.tagList?.items) return null;

  const items: FilterItem[] = data.tagList.items.map((industry: any) => ({
    id: industry.id,
    label: industry.name,
  }));

  return {
    id: "industry",
    label: "Industry",
    category: "Company",
    categoryColor: ENTITY_COLORS.company,
    filterData: {
      type: "multiSelect",
      filterKey: "industries",
      label: "Industry",
      items,
    },
  };
}

/**
 * Builds the Investment Tags filter from GraphQL data
 * Groups tags by tag class in a tree structure
 */
export function buildInvestmentTagsFilter(data: any): FilterDefinition | null {
  if (!data?.tagList?.items) return null;

  // Group tags by tag class
  const tagClassMap = new Map<
    number,
    { id: number; name: string; tags: Array<{ id: number; name: string }> }
  >();

  data.tagList.items.forEach((tag: any) => {
    if (!tag.tagClass) return;

    let tagClass = tagClassMap.get(tag.tagClass.id);
    if (!tagClass) {
      tagClass = {
        id: tag.tagClass.id,
        name: tag.tagClass.name,
        tags: [],
      };
      tagClassMap.set(tag.tagClass.id, tagClass);
    }

    tagClass.tags.push({
      id: tag.id,
      name: tag.name,
    });
  });

  // Build tree structure
  const treeData: AntdTreeNode[] = [];
  const keyToLabel = new Map<string, string>();

  Array.from(tagClassMap.values())
    .sort((a, b) => a.name.localeCompare(b.name))
    .forEach(tagClass => {
      const tagClassKey = `tagclass-${tagClass.id}`;
      keyToLabel.set(tagClassKey, tagClass.name);

      const children: AntdTreeNode[] = tagClass.tags
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(tag => {
          const tagKey = `tag-${tag.id}`;
          keyToLabel.set(tagKey, tag.name);
          return {
            key: tagKey,
            title: tag.name,
          };
        });

      treeData.push({
        key: tagClassKey,
        title: tagClass.name,
        children,
      });
    });

  if (treeData.length === 0) return null;

  return {
    id: "investmentTags",
    label: "Investment Tags",
    category: "Investment",
    categoryColor: ENTITY_COLORS.investment,
    filterData: {
      type: "multiSelectTree",
      filterKey: "investmentTags",
      label: "Investment Tags",
      treeData,
      keyToLabel,
    },
  };
}

/**
 * Builds the Investment List filter from GraphQL data
 * Uses entity filter type for search functionality
 */
export function buildInvestmentListFilter(): FilterDefinition {
  return {
    id: "investmentList",
    label: "Investment List",
    category: "Investment",
    categoryColor: ENTITY_COLORS.investment,
    filterData: {
      type: "entity",
      filterKey: "investmentListIds",
      label: "Investment List",
      entityType: "investmentSet",
    },
  };
}
